'use client';

import React, { KeyboardEvent, MouseEvent } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/solid';
import CategoryPill from '@/components/atoms/category-pill';
import Link from '@/components/atoms/link';
import useCustomRouter from '@/hooks/useCustomRouter';
import { classnames } from '@/utils/classnames/classnames';
import CategoryIcon from '../category-icon';
import { CategoryCardData } from '../../types';

const isExternal = (href?: string) => !!href && /^(https?:|mailto:|tel:)/i.test(href);

/**
 * CategoryCard — whole card navigates to the category root via an onClick region
 * (keyboard-activatable role="link"); sub-category chips and the BROWSE ALL link
 * are real anchors that `stopPropagation` so they navigate independently without
 * nesting `<a>` inside `<a>`.
 */
const CategoryCard = ({
  iconUrl,
  title,
  description,
  viewAllLabel = 'BROWSE ALL',
  viewAll,
  isHighlighted = false,
  subCategories = [],
}: CategoryCardData) => {
  const router = useCustomRouter();
  const href = viewAll?.href || '#';
  const external = isExternal(href) || !!viewAll?.openInNewTab;

  const navigate = () => {
    if (external) window.open(href, '_blank', 'noopener,noreferrer');
    else router.push(href);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Only the card itself activates — Enter/Space bubbling up from a focused
    // chip or the BROWSE ALL link must navigate to *their* href, not the card's.
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate();
    }
  };

  const stop = (event: MouseEvent) => event.stopPropagation();

  const browseAllClassName = classnames(
    'text-12 mt-auto flex w-fit items-center gap-1 rounded-xs pt-5 font-semibold tracking-wide uppercase focus-visible:ring-2 focus-visible:outline-none',
    isHighlighted ? 'text-white' : 'text-primary',
  );

  const browseAllInner = (
    <>
      {viewAllLabel}
      <ArrowRightIcon aria-hidden className="size-4 transition-transform group-hover:translate-x-1" />
    </>
  );

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={title}
      onClick={navigate}
      onKeyDown={onKeyDown}
      className={classnames(
        'group hover:shadow-400 flex h-full cursor-pointer flex-col rounded-lg border p-5 transition-shadow focus-visible:ring-2 focus-visible:outline-none lg:p-6',
        isHighlighted
          ? 'bg-primary border-primary text-white focus-visible:ring-white'
          : 'focus-visible:ring-primary border-gray-200 bg-white text-gray-800',
      )}
    >
      <CategoryIcon src={iconUrl} className={classnames('mb-4', isHighlighted ? 'text-white' : 'text-primary')} />

      <h3 className="text-18 lg:text-20 font-bold">{title}</h3>

      <p
        className={classnames(
          'text-14 mt-2 leading-normal',
          isHighlighted ? 'line-clamp-2 text-white/90' : 'line-clamp-3 text-gray-600',
        )}
      >
        {description}
      </p>

      {subCategories.length > 0 && (
        <ul className="mt-4 flex flex-wrap gap-2">
          {subCategories.map((chip, index) => (
            <li key={index}>
              {/* EWM-832 — the chip style now lives in `atoms/category-pill`,
                  shared with the PLP subcategory nav so the two cannot drift.
                  Rendered output is unchanged from EWM-264. */}
              <CategoryPill
                label={chip.label}
                href={chip.link?.href}
                openInNewTab={chip.link?.openInNewTab}
                isHighlighted={isHighlighted}
                onClick={stop}
              />
            </li>
          ))}
        </ul>
      )}

      {external ? (
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={stop} className={browseAllClassName}>
          {browseAllInner}
        </a>
      ) : (
        <Link href={href} onClick={stop} underlineOnHover={false} className={browseAllClassName}>
          {browseAllInner}
        </Link>
      )}
    </div>
  );
};

export default CategoryCard;
